import {
    type IncomeSplitExecution,
    IncomeSplitExecutionCarousel,
    IncomeSplitExecutionDetails,
    IncomeSplitExecutionRuleSets,
    IncomeSplitExecutions,
    IncomeSplitMonthCarousel,
    IncomeSplitMonthNavigator,
    type IncomeSplitOverviewData,
    type IncomeSplitOverviewHistory,
    type IncomeSplitTransfer,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import {
    selectedIncomeSplitExecutionAtom,
    selectedIncomeSplitExecutionIndexAtom,
    selectedIncomeSplitMonthAtom,
} from '@fruitful/income-split/src/state/IncomeSplitAtoms';
import { AnalyticsUserEvent } from '@fruitful/types';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import Animated, {
    Easing,
    LinearTransition,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import {
    ArrowRightIcon,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Icon,
    InsetContainer,
    InsetContainerVariant,
    ScreenContainer,
    Spinner,
    SplitIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../../common/hooks/useSendEmailToSupport';
import { useViewEvents } from '../../../../events/hooks/useViewEvents';
import { track } from '../../../../logger/Logger';
import { useRoadmapTabExperience } from '../../../../roadmap/hooks/useRoadmapTabExperience';
import { useGetIncomeSplitOverviewFromGlobalState } from '../hooks/useGetIncomeSplitOverviewFromGlobalState';
import { trackDepositDetailsClicked } from './trackDepositDetailsClicked';
// Helper function to check if all executions in a month are from money maps
const allExecutionsFromMoneyMap = (
    month: IncomeSplitOverviewHistory,
): boolean => {
    if (month.executions.length === 0) return false;
    return month.executions.every(
        (execution) =>
            (
                execution as IncomeSplitExecution & {
                    executionFromConfigSourcedFromMoneyMap?: boolean;
                }
            ).executionFromConfigSourcedFromMoneyMap === true,
    );
};

const monthShowsCircleCharts = (month: IncomeSplitOverviewHistory): boolean =>
    month.progress != null && allExecutionsFromMoneyMap(month);

const hasMoneyMapBackedIncomeSplitConfig = (
    data: IncomeSplitOverviewData,
): boolean =>
    data.configs.some(
        (config) =>
            config.status !== PaycheckRoutingStatus.DELETED &&
            (config.ruleSets ?? []).some((ruleSet) =>
                (ruleSet.rules ?? []).some(
                    (rule) =>
                        rule.moneyMapRuleId != null &&
                        rule.moneyMapRuleId !== '',
                ),
            ),
    );

const neverHadAnyIncomeSplitExecution = (
    data: IncomeSplitOverviewData,
): boolean => data.history.every((m) => m.executions.length === 0);

/** Empty donuts: after a month that had charts, or money-map config but no runs ever. */
const shouldShowEmptyMonthCharts = (
    data: IncomeSplitOverviewData,
    monthIndex: number,
): boolean => {
    const month = data.history[monthIndex];
    if (month == null) return false;
    if (month.progress != null || month.executions.length > 0) return false;

    const olderMonth = monthIndex > 0 ? data.history[monthIndex - 1] : null;
    const previousMonthHadCharts =
        olderMonth != null && monthShowsCircleCharts(olderMonth);

    const moneyMapButNeverRan =
        hasMoneyMapBackedIncomeSplitConfig(data) &&
        neverHadAnyIncomeSplitExecution(data);

    return previousMonthHadCharts || moneyMapButNeverRan;
};

/** Zero-state rings for money-map income split when the API has not attached month progress yet. */
const monthShouldShowZeroStateRingProgress = (
    data: IncomeSplitOverviewData,
    month: IncomeSplitOverviewHistory,
): boolean =>
    hasMoneyMapBackedIncomeSplitConfig(data) &&
    month.progress == null &&
    month.executions.length === 0;

// Helper function to check if any transfers in an execution have failed
const hasFailedTransfers = (
    execution: IncomeSplitExecution | null,
): boolean => {
    if (!execution?.transfers) return false;
    return execution.transfers.some(
        (transfer) =>
            (transfer as IncomeSplitTransfer & { status?: string }).status ===
                'FAIL' ||
            (transfer as IncomeSplitTransfer & { status?: string }).status ===
                'FAILED',
    );
};

const IncomeSplitDetails = ({ data }: { data: IncomeSplitOverviewData }) => {
    const [selectedIncomeSplitExecution, setSelectedIncomeSplitExecution] =
        useAtom(selectedIncomeSplitExecutionAtom);
    const [
        selectedIncomeSplitExecutionIndex,
        setSelectedIncomeSplitExecutionIndex,
    ] = useAtom(selectedIncomeSplitExecutionIndexAtom);
    const [selectedIncomeSplitMonth, setSelectedIncomeSplitMonth] = useAtom(
        selectedIncomeSplitMonthAtom,
    );

    const [currentMonthIndex, setCurrentMonthIndex] = useState(() => {
        if (selectedIncomeSplitMonth == null) {
            return data.history.length - 1;
        }

        const selectedMonthIndex = data.history.findIndex(
            (month) => month.month === selectedIncomeSplitMonth.month,
        );

        return selectedMonthIndex === -1
            ? data.history.length - 1
            : selectedMonthIndex;
    });
    useEffect(() => {
        setSelectedIncomeSplitMonth(
            data.history[currentMonthIndex] as IncomeSplitOverviewHistory,
        );
    }, [currentMonthIndex]);

    useEffect(() => {
        if (selectedIncomeSplitExecutionIndex != null) {
            setSelectedIncomeSplitExecution(
                selectedIncomeSplitMonth?.executions[
                    selectedIncomeSplitExecutionIndex
                ] as IncomeSplitExecution,
            );
            setSelectedIncomeSplitExecutionIndex(
                selectedIncomeSplitExecutionIndex,
            );
        }
    }, [selectedIncomeSplitExecutionIndex]);

    // control initial visibility for web compatibility
    const opacity = useSharedValue(0);
    const translateY = useSharedValue(20);

    // track which carousel to show (no animation, instant switch)
    const isShowingExecution = useSharedValue(
        selectedIncomeSplitExecution != null ? 1 : 0,
    );

    useEffect(() => {
        opacity.value = withDelay(
            100,
            withTiming(1, {
                duration: 250,
                easing: Easing.bezier(0.65, 0, 0.35, 1),
            }),
        );
        translateY.value = withDelay(
            100,
            withTiming(0, {
                duration: 250,
                easing: Easing.bezier(0.65, 0, 0.35, 1),
            }),
        );
    }, []);

    // update which carousel to show with a very quick transition to prevent flashing on web
    useEffect(() => {
        const shouldShowExecution = selectedIncomeSplitExecution != null;
        if (Platform.OS === 'web') {
            // On web, use sequence to hide one before showing the other to prevent overlap/flashing
            if (shouldShowExecution) {
                // First hide month (set to 0), then show execution (set to 1) after brief delay
                isShowingExecution.value = withSequence(
                    withTiming(0, { duration: 0 }),
                    withDelay(
                        30,
                        withTiming(1, {
                            duration: 50,
                            easing: Easing.linear,
                        }),
                    ),
                );
            } else {
                // Hide execution instantly to prevent flashing when switching back to month
                // The month carousel will appear immediately since its opacity is !isShowingExecution.value
                isShowingExecution.value = 0;
            }
        } else {
            // On mobile, instant switch
            isShowingExecution.value = shouldShowExecution ? 1 : 0;
        }
    }, [selectedIncomeSplitExecution, isShowingExecution]);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            opacity: opacity.value,
            transform: [{ translateY: translateY.value }],
        };
    });

    const monthCarouselStyle = useAnimatedStyle(() => {
        const showMonth = !isShowingExecution.value;
        return {
            opacity: showMonth ? 1 : 0,
            position: 'absolute' as const,
            top: 16,
            left: 0,
            right: 0,
            pointerEvents: showMonth ? ('auto' as const) : ('none' as const),
        };
    });

    const executionCarouselStyle = useAnimatedStyle(() => {
        const showExecution = isShowingExecution.value;
        return {
            opacity: showExecution ? 1 : 0,
            position: 'absolute' as const,
            top: 16,
            left: 0,
            right: 0,
            pointerEvents: showExecution
                ? ('auto' as const)
                : ('none' as const),
        };
    });

    const monthKeysWithEmptyProgressCharts = useMemo(() => {
        const keys = new Set<string>();
        data.history.forEach((m, i) => {
            if (shouldShowEmptyMonthCharts(data, i)) {
                keys.add(m.month);
            }
            if (monthShouldShowZeroStateRingProgress(data, m)) {
                keys.add(m.month);
            }
        });
        return keys;
    }, [data]);

    const showMonthProgressStrip =
        selectedIncomeSplitExecution == null &&
        selectedIncomeSplitMonth != null &&
        (monthKeysWithEmptyProgressCharts.has(selectedIncomeSplitMonth.month) ||
            selectedIncomeSplitMonth.progress != null);

    const showExecutionProgressStrip =
        selectedIncomeSplitExecution != null &&
        selectedIncomeSplitMonth?.progress != null &&
        (
            selectedIncomeSplitExecution as IncomeSplitExecution & {
                executionFromConfigSourcedFromMoneyMap?: boolean;
            }
        ).executionFromConfigSourcedFromMoneyMap === true;

    const showProgressChartStrip =
        showMonthProgressStrip || showExecutionProgressStrip;

    return (
        <InsetContainer variant={InsetContainerVariant.DARK}>
            <YStack
                paddingBottom={
                    (selectedIncomeSplitMonth?.executions ?? []).length > 0
                        ? 0
                        : 16
                }
            >
                {!selectedIncomeSplitExecution && (
                    <IncomeSplitMonthNavigator
                        data={data}
                        currentMonthIndex={currentMonthIndex}
                        setCurrentMonthIndex={setCurrentMonthIndex}
                    />
                )}
                {showProgressChartStrip && (
                    <Animated.View
                        style={[
                            {
                                width: '100%',
                                height: 162,
                                paddingTop: 16,
                                position: 'relative',
                            },
                            animatedStyle,
                        ]}
                        layout={LinearTransition.duration(250)}
                    >
                        <Animated.View style={monthCarouselStyle}>
                            <IncomeSplitMonthCarousel
                                data={data.history}
                                initialIndex={currentMonthIndex}
                                monthKeysWithEmptyProgressCharts={
                                    monthKeysWithEmptyProgressCharts
                                }
                                setCurrentMonthIndex={setCurrentMonthIndex}
                            />
                        </Animated.View>
                        <Animated.View style={executionCarouselStyle}>
                            {(selectedIncomeSplitMonth?.executions ?? [])
                                .length > 0 && (
                                <IncomeSplitExecutionCarousel
                                    key={`execution-${selectedIncomeSplitExecution?.id ?? 'none'}-${selectedIncomeSplitExecutionIndex ?? 0}`}
                                    initialIndex={
                                        selectedIncomeSplitExecutionIndex ?? 0
                                    }
                                    setCurrentExecutionIndex={
                                        setSelectedIncomeSplitExecutionIndex
                                    }
                                />
                            )}
                        </Animated.View>
                    </Animated.View>
                )}
                {selectedIncomeSplitExecution ? (
                    <IncomeSplitExecutionDetails
                        execution={selectedIncomeSplitExecution}
                    />
                ) : (
                    <IncomeSplitExecutions
                        month={
                            selectedIncomeSplitMonth as IncomeSplitOverviewHistory
                        }
                        onPressExecution={trackDepositDetailsClicked}
                    />
                )}
            </YStack>
        </InsetContainer>
    );
};

const IncomeSplitRulesButton = () => {
    const tabExperience = useRoadmapTabExperience();

    if (tabExperience === 'money_system') {
        return null;
    }

    return (
        <InsetContainer
            variant={InsetContainerVariant.DARK}
            padding={16}
            onPress={() => {
                track(AnalyticsUserEvent.INCOME_SPLIT_RULES_CLICKED);
                router.navigate('/income-split');
            }}
            cursor="pointer"
        >
            <XStack alignItems="center" justifyContent="space-between">
                <XStack gap={12} alignItems="center">
                    <View rotate="180deg">
                        <Icon icon={SplitIcon} size={24} />
                    </View>
                    <YStack gap={4}>
                        <BodyMediumRegular>
                            Income Split rules
                        </BodyMediumRegular>
                        <BodySmallRegular color="$gray1000">
                            View or manage these rules
                        </BodySmallRegular>
                    </YStack>
                </XStack>
                <Icon icon={ArrowRightIcon} size={24} />
            </XStack>
        </InsetContainer>
    );
};

const ViewAllMonthsButton = () => {
    return (
        <View alignItems="center">
            <View
                paddingVertical={12}
                paddingHorizontal={20}
                borderRadius={100}
                backgroundColor="$gray200"
                onPress={() => {
                    router.navigate({
                        pathname: '/income-split/all-months',
                    });
                }}
                cursor="pointer"
            >
                <BodyMediumRegular>View all months</BodyMediumRegular>
            </View>
        </View>
    );
};

const IncomeSplitOverview = () => {
    const sendEmailToSupport = useSendEmailToSupport();
    const { executionId } = useLocalSearchParams<{ executionId: string }>();
    const { trackViewOnce } = useViewEvents();

    const { data, isLoading, isError } =
        useGetIncomeSplitOverviewFromGlobalState();

    const [selectedIncomeSplitMonth, setSelectedIncomeSplitMonth] = useAtom(
        selectedIncomeSplitMonthAtom,
    );

    const [selectedIncomeSplitExecution, setSelectedIncomeSplitExecution] =
        useAtom(selectedIncomeSplitExecutionAtom);
    const setSelectedIncomeSplitExecutionIndex = useSetAtom(
        selectedIncomeSplitExecutionIndexAtom,
    );

    useEffect(() => {
        if (isLoading || !data || isError) {
            return;
        }

        if (executionId != null && selectedIncomeSplitExecution == null) {
            return;
        }

        const viewName =
            selectedIncomeSplitExecution == null
                ? AnalyticsUserEvent.INCOME_SPLIT_OVERVIEW_VIEW
                : AnalyticsUserEvent.INCOME_SPLIT_DEPOSIT_DETAILS_VIEW;

        trackViewOnce(viewName);
    }, [
        data,
        executionId,
        isError,
        isLoading,
        selectedIncomeSplitExecution,
        trackViewOnce,
    ]);

    useEffect(() => {
        if (!isLoading && data && data.history.length > 0 && executionId) {
            for (const month of data.history) {
                const index = month.executions.findIndex(
                    (execution) => execution.id === executionId,
                );
                if (index !== -1) {
                    setSelectedIncomeSplitMonth(month);
                    setSelectedIncomeSplitExecution(
                        month.executions[index] as IncomeSplitExecution,
                    );
                    setSelectedIncomeSplitExecutionIndex(index);
                    break;
                }
            }
        }
    }, [data, isLoading, executionId, setSelectedIncomeSplitMonth]);

    useEffect(() => {
        if (!isLoading && data && data.history.length > 0 && !executionId) {
            setSelectedIncomeSplitMonth(
                data.history[
                    data.history.length - 1
                ] as IncomeSplitOverviewHistory,
            );
        }
    }, [data, isLoading]);

    useFocusEffect(
        useCallback(() => {
            return () => {
                setSelectedIncomeSplitExecution(null);
                setSelectedIncomeSplitExecutionIndex(null);
            };
        }, []),
    );

    if (isLoading) {
        return (
            <ScreenContainer
                title="Income Split"
                hasBackButton
                onFallbackRoute="/home/banking"
            >
                <YStack
                    gap={16}
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    <Spinner />
                </YStack>
            </ScreenContainer>
        );
    }

    if (isError || !data) {
        return (
            <ScreenContainer
                title="Income Split"
                hasBackButton
                onFallbackRoute="/home/banking"
            >
                <YStack
                    gap={16}
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    <BodyMediumRegular>
                        Unable to load Income Split data. Please try again
                        later.
                    </BodyMediumRegular>
                </YStack>
            </ScreenContainer>
        );
    }

    if (data.history.length === 0) {
        return (
            <ScreenContainer
                title="Income Split"
                hasBackButton
                onFallbackRoute="/home/banking"
            >
                <YStack
                    gap={16}
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                >
                    <BodyMediumRegular>
                        No Income Split data available yet.
                    </BodyMediumRegular>
                </YStack>
            </ScreenContainer>
        );
    }

    const months = data.history.filter((month) => month.executions.length > 0);

    // Check if all executions across all months are empty
    const hasAnyExecutions = months.length > 0;
    const hasPreviousMonths = months.length > 1;

    if (!hasAnyExecutions) {
        return (
            <ScreenContainer
                title="Income Split"
                hasBackButton
                onFallbackRoute="/home/banking"
            >
                <YStack gap={16} alignItems="center" justifyContent="center">
                    <BodyMediumEmphasized textAlign="center">
                        No Income Splits...yet
                    </BodyMediumEmphasized>
                    <BodySmallRegular textAlign="center" color="$gray1000">
                        When a qualifying deposit arrives, you&apos;ll be able
                        to see the details of your split here
                    </BodySmallRegular>
                    <YStack width="100%" marginTop={24}>
                        <IncomeSplitRulesButton />
                    </YStack>
                </YStack>
            </ScreenContainer>
        );
    }

    return (
        <ScreenContainer
            title={selectedIncomeSplitExecution?.description ?? 'Income Split'}
            numLinesForTitle={1}
            hasBackButton={!selectedIncomeSplitExecution}
            hasCloseButton={!!selectedIncomeSplitExecution}
            onClose={() => {
                if (executionId) {
                    // Opened via deep link / push notification, there may be no history to go back to
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.navigate('/home/banking');
                    }
                    return;
                }
                setSelectedIncomeSplitExecution(null);
                setSelectedIncomeSplitExecutionIndex(null);
            }}
            onFallbackRoute="/home/banking"
        >
            {selectedIncomeSplitMonth && (
                <YStack gap={16}>
                    <IncomeSplitDetails data={data} />
                    {/* TODO: build out view all page */}
                    {!selectedIncomeSplitExecution && (
                        <>
                            {hasPreviousMonths && <ViewAllMonthsButton />}
                            <IncomeSplitRulesButton />
                        </>
                    )}
                    {selectedIncomeSplitExecution && (
                        <>
                            {hasFailedTransfers(
                                selectedIncomeSplitExecution,
                            ) && (
                                <YStack
                                    padding={16}
                                    gap={12}
                                    borderRadius={16}
                                    backgroundColor="$orange300"
                                >
                                    <YStack gap={8}>
                                        <BodyMediumEmphasized color="$orange1100">
                                            Income Split incomplete
                                        </BodyMediumEmphasized>
                                        <BodySmallRegular color="$orange1000">
                                            Something went wrong with one or
                                            more of your Income Split transfers
                                            below. Any funds not successfully
                                            transferred remained in your{' '}
                                            {
                                                selectedIncomeSplitExecution.sourceAccountName
                                            }{' '}
                                            account and will not count against
                                            your contribution limits.
                                        </BodySmallRegular>
                                    </YStack>
                                    <View
                                        onPress={() => {
                                            void sendEmailToSupport({
                                                subject:
                                                    'Income Split Transfer Issue',
                                            });
                                        }}
                                        cursor="pointer"
                                    >
                                        <BodySmallEmphasized
                                            color="$orange1100"
                                            textDecorationLine="underline"
                                        >
                                            Contact Fruitful Hospitality
                                        </BodySmallEmphasized>
                                    </View>
                                </YStack>
                            )}
                            <IncomeSplitExecutionRuleSets
                                incomeSplitOverview={data}
                                onExpandRuleSet={(ruleSetName) => {
                                    track(
                                        AnalyticsUserEvent.INCOME_SPLIT_RULE_SET_EXPANDED,
                                        {
                                            ruleSetName,
                                        },
                                    );
                                }}
                            />
                        </>
                    )}
                </YStack>
            )}
        </ScreenContainer>
    );
};

export default IncomeSplitOverview;
